/**
 * React MetricBackend Module Initializer
 *
 * Initializes the React MetricBackend module when feature flags are enabled.
 * This approach keeps Angular code untouched for backward compatibility.
 */

import backendApi from "@results/services/apiCall/backendApi";
import ApiPath from "@viz-ui/services/apiPath/apiPathService";
import Metric from "@viz-ui/models/metric/metric";
import MetricSparkline from "@viz-ui/models/metric/metricSparkline";
import MetricSummary from "@viz-ui/models/metric/metricSummary";
import QueryClient from "@viz-ui/services/query/queryClient/queryClient";
import QueryGenerator from "@viz-ui/services/query/queryGenerator/queryGenerator";
import QueryErrorInterpreter from "@viz-ui/services/query/queryErrorInterpreter/queryErrorInterpreter.service";
import MetricDataAdapter from "@viz-ui/services/query/queryAdapters/metricDataAdapter";
import TableDataAdapter from "@viz-ui/services/query/queryAdapters/tableDataAdapter";
import { MetricBackendModule } from "./metricBackendModule";

// Timeout constants for better maintainability
const ANGULAR_READY_RETRY_TIMEOUT_MS = 10;
const ANGULAR_INIT_DELAY_TIMEOUT_MS = 0;

function initializeReactMetricBackend() {
  // Wait for Angular and DOM to be ready
  if (typeof angular === "undefined") {
    // Angular not ready yet, retry later with a short delay
    setTimeout(initializeReactMetricBackend, ANGULAR_READY_RETRY_TIMEOUT_MS);
    return;
  }

  // If document is loading, wait for it
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initializeReactMetricBackend();
    });
    return;
  }

  try {
    const injector = angular.element(document.body).injector();
    if (!injector) {
      // Angular injector not ready yet, retry later with a short delay
      // Use setTimeout here as injector is not available yet
      // eslint-disable-next-line angular/timeout-service
      setTimeout(initializeReactMetricBackend, ANGULAR_READY_RETRY_TIMEOUT_MS);
      return;
    }

    const AppConfig = injector.get("AppConfig");

    // Only initialize React module if the complete module migration is enabled
    // Individual service flags will be handled by the module internally
    if (AppConfig.features && AppConfig.features.migratedMetricBackendModule) {
      // Ensure ApiPath is initialized with AppConfig to avoid race conditions
      if (ApiPath && typeof ApiPath.initialize === "function") {
        ApiPath.initialize(AppConfig);
      }

      try {
        // Initialize React module with all dependencies
        const metricBackendModule = new MetricBackendModule({
          AppConfig,
          EventService: injector.get("EventService"),
          backendApi,
          ApiPath,
          Metric,
          MetricSparkline,
          MetricSummary,
          QueryClient,
          QueryGenerator,
          QueryErrorInterpreter,
          MetricDataAdapter,
          TableDataAdapter,
          DataFilter: injector.get("DataFilter"),
          TableAdapter: injector.get("TableAdapter"),
        });

        // Store reference to avoid unused variable warning
        if (metricBackendModule) {
          // React module initialized successfully
        }
      } catch (error) {
        // React initialization failed - Angular will handle everything automatically
        // No action needed as Angular module is already loaded and running
      }
    }
    // If migratedMetricBackendModule is false, Angular handles everything (no React initialization)
  } catch (error) {
    // Failed to get Angular services - Angular will handle everything automatically
    // No action needed as Angular module is already loaded and running
  }
}

// Auto-initialize
if (typeof window !== "undefined") {
  initializeReactMetricBackend();
}

export default initializeReactMetricBackend;
